import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Check, X, PencilSimple } from "@phosphor-icons/react";

/**
 * Click-to-edit unit cost cell. Saves on Enter or check button; cancels on Escape or x.
 * Disabled rendering = read-only display.
 */
export default function InlineCostCell({ part, editable, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(part.unit_cost ?? 0));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setVal(String(part.unit_cost ?? 0));
  }, [part.unit_cost]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
    if (editing) inputRef.current?.select();
  }, [editing]);

  if (!editable) {
    return (
      <span className="font-mono text-xs">${Number(part.unit_cost || 0).toFixed(2)}</span>
    );
  }

  const save = async () => {
    const n = Number(val);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a non-negative number");
      return;
    }
    if (Math.abs(n - Number(part.unit_cost || 0)) < 0.005) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/parts/${part.id}`, { unit_cost: n });
      toast.success(`Cost updated · $${n.toFixed(2)}`);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setVal(String(part.unit_cost ?? 0));
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Edit cost"
        data-testid={`inline-cost-${part.sku}`}
        className="group inline-flex items-center gap-1 font-mono text-xs hover:text-[#FF9800]"
      >
        ${Number(part.unit_cost || 0).toFixed(2)}
        <PencilSimple size={11} weight="bold" className="opacity-0 group-hover:opacity-100 text-zinc-400 group-hover:text-[#FF9800]" />
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <span className="font-mono text-xs text-zinc-500">$</span>
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          else if (e.key === "Escape") cancel();
        }}
        disabled={saving}
        data-testid={`inline-cost-input-${part.sku}`}
        className="w-20 px-2 py-1 border border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-xs text-right"
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        title="Save"
        data-testid={`inline-cost-save-${part.sku}`}
        className="p-1 bg-[#FF9800] text-white hover:bg-[#E68900] disabled:opacity-60"
      >
        <Check size={11} weight="bold" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        title="Cancel"
        data-testid={`inline-cost-cancel-${part.sku}`}
        className="p-1 border border-zinc-300 hover:bg-zinc-100"
      >
        <X size={11} weight="bold" />
      </button>
    </div>
  );
}
